import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

function About() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0,
        delayChildren: 0,
        when: 'beforeChildren',
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.1 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.1,
        ease: 'easeOut',
      },
    },
  };

  const highlightItemVariants = {
    hidden: { opacity: 0, x: -20, rotate: -5 },
    visible: {
      opacity: 1,
      x: 0,
      rotate: 0,
      transition: {
        duration: 0.1,
        ease: 'easeOut',
      },
    },
  };

  const circleVariants = {
    hidden: { opacity: 0, scale: 0, rotate: 45 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.1,
        ease: 'easeOut',
        type: 'spring',
        stiffness: 80,
      },
    },
  };

  const scrollRef = useRef(null);
  useScroll({
    target: scrollRef,
    offset: ['start 0.8', 'start 0.5'],
  });

  const [ref, inView] = useInView({ threshold: 0.5, triggerOnce: true });
  const controls = useAnimation();
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    if (inView && !animated) {
      controls.start('visible');
      setAnimated(true);
    }
  }, [controls, inView, animated]);

  return (
    <section
      id="about"
      className="about-section"
      ref={scrollRef}
      style={{
        padding: '60px 0',
        backgroundColor: '#FFFFFF',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <motion.div
        className="decorative-circle-top"
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={circleVariants}
      />
      <motion.div
        className="decorative-circle-bottom"
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={circleVariants}
        transition={{ delay: 0.2 }}
      />

      <div className="about-container" ref={ref}>
        <motion.div
          className="main-content"
          initial="hidden"
          animate={controls}
          variants={containerVariants}
        >
          <motion.div variants={itemVariants}>
            <h2 className="about-title">
              Who We Are
              <span className="title-underline"></span>
            </h2>
          </motion.div>

          <motion.p variants={itemVariants} className="about-text">
            AIC was founded on a simple belief: when Africans worldwide come together, we can unlock unprecedented opportunities. Our mission is to create a safe, transparent platform that allows members to pool funds, invest collectively, and build generational wealth.
          </motion.p>

          <motion.p variants={itemVariants} className="about-text">
            We believe in democratically managed investments, empowering each shareholder with a real voice in where our money goes. By blending community spirit, sound financial principles, and a passion for growth, we're shaping a new era of African-led prosperity.
          </motion.p>
        </motion.div>

        <motion.div
          className="highlights-container"
          initial="hidden"
          animate={controls}
          variants={containerVariants}
        >
          <motion.div
            className="highlight-decorative"
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={circleVariants}
            transition={{ delay: 0.4 }}
          />
          <motion.h3 variants={itemVariants} className="highlight-title" style={{ fontWeight: 'bold' }}>
            Key Highlights
          </motion.h3>

          <motion.ul variants={containerVariants} className="highlight-list">
            {[
              '<strong>Unity in Diversity:</strong> Open to Africans from every corner of the globe.',
              '<strong>Monthly Contributions:</strong> Accessible entry, so everyone can grow together.',
              '<strong>Solid Governance:</strong> Clear rules, majority voting, and a single Manager for streamlined operations.',
              '<strong>Global Opportunities:</strong> Strategic investments that extend far beyond borders.',
            ].map((item, index) => (
              <motion.li
                key={index}
                variants={highlightItemVariants}
                className="highlight-item"
                whileHover={{ scale: 1.02, backgroundColor: 'rgba(255, 217, 102, 0.3)' }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <span className="highlight-number">{index + 1}</span>
                <span className="highlight-text" dangerouslySetInnerHTML={{ __html: item }} />
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>
      </div>

      <style jsx>{`
        .about-container {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          flex-direction: row;
          gap: 40px;
          align-items: flex-start;
          position: relative;
          z-index: 1;
          padding: 0 20px;
        }

        .main-content {
          flex: 2;
        }

        .about-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1f1f1f;
          margin-bottom: 2rem;
          font-family: 'Poppins', sans-serif;
          position: relative;
          display: inline-block;
          line-height: 1.2;
        }

        .title-underline {
          position: absolute;
          bottom: -10px;
          left: 0;
          width: 60px;
          height: 4px;
          background-color: #ffc300;
          border-radius: 2px;
        }

        .about-text {
          font-size: 1.125rem;
          line-height: 1.8;
          color: #4b5563;
          margin-bottom: 1.5rem;
          font-family: 'Poppins', sans-serif;
        }

        .highlights-container {
          flex: 1;
          background: linear-gradient(145deg, #fff9e6, #fffdf5);
          padding: 35px;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
          position: relative;
          overflow: visible;
          z-index: 2;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .highlights-container:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
        }

        .highlight-decorative {
          position: absolute;
          top: 0;
          right: 0;
          width: 100px;
          height: 100px;
          background-color: rgba(255, 195, 0, 0.2);
          border-radius: 0 0 0 100%;
          z-index: 0;
        }

        .highlight-title {
          font-size: 1.8rem;
          color: #1a1a1a;
          margin-bottom: 2rem;
          font-family: 'Poppins', sans-serif;
        }

        .highlight-list {
          padding-left: 0;
          list-style-type: none;
        }

        .highlight-item {
          font-size: 1.05rem;
          font-family: 'Poppins', sans-serif;
          margin-bottom: 1.8rem;
          display: flex;
          align-items: center;
          line-height: 1.6;
          background: rgba(255, 243, 202, 0.3);
          border-left: 4px solid #ffc300;
          border-radius: 8px;
          padding: 12px 15px;
          transition: all 0.3s ease;
        }

        .highlight-number {
          display: inline-flex;
          width: 30px;
          height: 30px;
          background-color: #ffc300;
          color: #1a1a1a;
          border-radius: 50%;
          text-align: center;
          line-height: 30px;
          margin-right: 14px;
          flex-shrink: 0;
          font-weight: bold;
          justify-content: center;
          align-items: center;
          box-shadow: 0 4px 8px rgba(255, 195, 0, 0.4);
          font-size: 0.95rem;
          animation: bounceIn 0.6s ease both;
        }

        .highlight-text {
          flex: 1;
        }

        .highlight-text strong {
          font-weight: 700;
        }

        @keyframes bounceIn {
          0% {
            transform: scale(0.5);
            opacity: 0;
          }
          60% {
            transform: scale(1.1);
            opacity: 1;
          }
          100% {
            transform: scale(1);
          }
        }

        .decorative-circle-top {
          position: absolute;
          top: -50px;
          right: -50px;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background-color: rgba(255, 195, 0, 0.1);
          z-index: 0;
        }

        .decorative-circle-bottom {
          position: absolute;
          bottom: 20%;
          left: -80px;
          width: 160px;
          height: 160px;
          border-radius: 50%;
          background-color: rgba(255, 195, 0, 0.05);
          z-index: 0;
        }

        @media (max-width: 768px) {
          .about-container {
            flex-direction: column;
            gap: 30px;
            padding: 0 15px;
          }

          .about-title {
            font-size: 2rem !important;
          }

          .about-text {
            font-size: 1rem !important;
          }

          .highlights-container {
            width: 100%;
            padding: 25px;
          }

          .highlight-item {
            font-size: 0.95rem;
          }

          .highlight-number {
            width: 24px;
            height: 24px;
            font-size: 0.9rem;
          }

          .decorative-circle-top,
          .decorative-circle-bottom,
          .highlight-decorative {
            display: none;
          }
        }
      `}</style>
    </section>
  );
}

export default About;